import {
  chargeLocatorChargeDetailsViewParams,
  chargeLocatorChargeNotFoundClickContactCustomerCareParams,
  chargeLocatorChargeNotFoundViewParams,
  chargeLocatorClickSearchParams,
  chargeLocatorConsolidatedInvoiceViewParams,
  chargeLocatorErrorParams,
} from '@wix/bi-logger-pre/v2/types';
import {
  chargeLocatorChargeDetailsClickSearchAgain,
  chargeLocatorChargeDetailsView,
  chargeLocatorChargeNotFoundClickContactCustomerCare,
  chargeLocatorChargeNotFoundView,
  chargeLocatorClickSearch,
  chargeLocatorConsolidatedInvoiceView,
  chargeLocatorError,
  chargeLocatorFaqCancelSubscription,
  chargeLocatorFaqClick,
  chargeLocatorFaqClickContactCustomerCare,
  chargeLocatorLoad,
} from '@wix/bi-logger-pre/v2';
import webBiLogger from '@wix/web-bi-logger';
import { Logger } from '@wix/web-bi-logger/dist/src/types';
import { IAppContext } from '../client';

interface DeafultBIProps {
  referralAdditionalInfo: string;
}

type OmitInternalBIArgs<T> = Omit<T, 'src' | keyof DeafultBIProps>;

export class BiLoggerService {
  private readonly schemaLogger: Logger;
  private deafultBIProps: Partial<DeafultBIProps> = {};

  constructor(appContext: IAppContext) {
    this.deafultBIProps = {
      referralAdditionalInfo: appContext.referalInfo,
    };
    this.schemaLogger = webBiLogger.factory().logger();
  }

  readonly chargeLocatorLoad = () =>
    this.schemaLogger.report(chargeLocatorLoad(this.deafultBIProps));

  readonly clickSearch = ({
    invoiceNumber,
  }: OmitInternalBIArgs<chargeLocatorClickSearchParams>) =>
    this.schemaLogger.report(
      chargeLocatorClickSearch({
        ...this.deafultBIProps,
        invoiceNumber,
      })
    );

  readonly faqItemExpandCollapse = (content: string) =>
    this.schemaLogger.report(
      chargeLocatorFaqClick({
        ...this.deafultBIProps,
        content: JSON.stringify(content),
      })
    );

  readonly chargeDetailsView = ({
    amount,
    invoiceNumber,
    paymentMethod,
    productTypeName,
    subscriptionId,
  }: OmitInternalBIArgs<chargeLocatorChargeDetailsViewParams>) =>
    this.schemaLogger.report(
      chargeLocatorChargeDetailsView({
        ...this.deafultBIProps,
        amount,
        invoiceNumber,
        paymentMethod,
        productTypeName,
        subscriptionId,
      })
    );

  readonly chargeNotFoundView = ({
    invoiceNumber,
  }: OmitInternalBIArgs<chargeLocatorChargeNotFoundViewParams>) =>
    this.schemaLogger.report(
      chargeLocatorChargeNotFoundView({
        ...this.deafultBIProps,
        invoiceNumber,
      })
    );

  readonly consolodatedView = ({
    invoiceNumber,
  }: OmitInternalBIArgs<chargeLocatorConsolidatedInvoiceViewParams>) =>
    this.schemaLogger.report(
      chargeLocatorConsolidatedInvoiceView({
        ...this.deafultBIProps,
        invoiceNumber,
      })
    );

  readonly notFoundClick = ({
    invoiceNumber,
  }: OmitInternalBIArgs<chargeLocatorChargeNotFoundClickContactCustomerCareParams>) =>
    this.schemaLogger.report(
      chargeLocatorChargeNotFoundClickContactCustomerCare({
        ...this.deafultBIProps,
        invoiceNumber,
      })
    );

  readonly searchAgainClick = () =>
    this.schemaLogger.report(chargeLocatorChargeDetailsClickSearchAgain({}));

  // TODO(PREM2-30172): wire to chargeLocatorChargeDetailsClickLogin from @wix/bi-logger-pre/v2 once the schema event lands.
  // Defined as a regular method (not a class-field arrow) so tests can spy
  // via BiLoggerService.prototype. The call site wraps the invocation in an
  // arrow, so `this`-binding is not relied on.
  loginToAccountClick(): void {
    // intentional empty stub — body added when the BI schema event ships.
  }

  readonly logError = ({
    errorInfo,
  }: OmitInternalBIArgs<chargeLocatorErrorParams>) =>
    this.schemaLogger.report(
      chargeLocatorError({ ...this.deafultBIProps, errorInfo })
    );

  readonly faqInAnswerClick = (type: 'customerCare' | 'cancelSubscription') =>
    type === 'customerCare'
      ? this.schemaLogger.report(chargeLocatorFaqClickContactCustomerCare({}))
      : this.schemaLogger.report(chargeLocatorFaqCancelSubscription({}));
}
